import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { POSTER_SLOT_NAME, type MusicSource, type PosterMvCandidate, type PosterSlot, type PosterStateResponse } from "@studio/shared";
import { InlineSpinner } from "../../components/InlineSpinner";
import { MySongPicker } from "../../components/MySongPicker";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../../components/StudioShell";
import { ApiError, apiRequest } from "../../lib/api";
import { useImeSafeInput } from "../../lib/ime";
import { pendingGiftsRevision, subscribePendingGifts } from "../../lib/gifts";
import { usePoster } from "../../poster/api";
import { PosterPreview } from "../../poster/PosterPreview";
import { goPosterStep } from "../../poster/steps";
import { PosterResumeGate } from "./PosterResumeGate";
import { videoFailureMessage } from "../../lib/videoFailure";

/**
 * 포스터 3단계 「노래 · AR」(목업 s6).
 * 🔑 노래는 그림 수업의 「노래 붙이기」 문(곡 코드·음원 파일)을 그대로 쓴다.
 * 🔑 AR 로 나올 것 = 그림 + 노래(비용 0) · AI 영상 + 노래(1편 + 다시 1번) · 내 뮤직비디오(비용 0).
 *    AI 영상 재료는 AI 가 그린 칸 그림만 — 올린 사진은 고를 수 없다(실제 인물 딥페이크·외부 전송 차단).
 * 🔑 영상은 포스터 위에 붙지 않고 폰 화면 가운데에 뜬다(키링 AR 의 떨림 교훈).
 */

const SLOT_TITLE = POSTER_SLOT_NAME;
const songCodePattern = /^[A-HJ-NP-Z2-9]{13}$/u;

function duration(seconds: number): string {
  const rounded = Math.max(0, Math.round(seconds));
  return `${Math.floor(rounded / 60)}분 ${String(rounded % 60).padStart(2, "0")}초`;
}

/**
 * 완성된 AI 영상을 크게 재생해 본다(2026-10-04 jery) — 미리보기는 소리 없이 작게 반복만 돼서 «완성작» 을 볼 수 없었다.
 * 🔑 노래를 붙였으면 AR 에서처럼 노래가 같이 나온다. 영상(5초)은 반복하고 노래는 이어서 흐른다.
 */
function PosterVideoDialog({ clipUrl, musicAssetId, onClose }: { clipUrl: string; musicAssetId: string | null; onClose: () => void }) {
  const video = useRef<HTMLVideoElement | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!musicAssetId) return undefined;
    let cancelled = false;
    void apiRequest<{ url: string }>(`/api/assets/${encodeURIComponent(musicAssetId)}/url`)
      .then((result) => { if (!cancelled) setAudioUrl(result.url); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [musicAssetId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="exit-dialog-backdrop" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="exit-dialog poster-video-dialog" role="dialog" aria-modal="true" aria-labelledby="poster-video-title">
        <button className="button-ghost exit-dialog-close" type="button" onClick={onClose}>
          <span aria-hidden="true">×</span>닫기
        </button>
        <h2 id="poster-video-title">완성한 영상</h2>
        <video
          ref={video}
          className="poster-video-full"
          src={clipUrl}
          controls
          autoPlay
          loop
          playsInline
          onPlay={() => { void audio.current?.play().catch(() => undefined); }}
          onPause={() => audio.current?.pause()}
        />
        {audioUrl ? (
          <audio
            ref={audio}
            src={audioUrl}
            preload="auto"
            onCanPlay={() => { if (video.current && !video.current.paused) void audio.current?.play().catch(() => undefined); }}
          />
        ) : null}
        <p>{musicAssetId ? "AR 로 비추면 이 영상이 노래와 함께 나와요." : "AR 로 비추면 이 영상이 나와요. 노래를 붙이면 같이 나와요."}</p>
      </div>
    </div>
  );
}

/** 「어떻게 움직일까요?」 — 서버 상한(POSTER_VIDEO_PROMPT_MAX_CHARS)과 같다. */
const POSTER_MOTION_MAX_CHARS = 100;
const MOTION_EXAMPLES = ["강아지가 꼬리를 흔들어요", "꽃잎이 바람에 날려요", "별이 반짝반짝 빛나요"];

export function PosterSongArPage({ workId }: { workId: string }) {
  const poster = usePoster(workId);
  if (poster.loading) return <LoadingPage label="노래 · AR 을 불러오는 중..." />;
  if (!poster.data) return <ErrorPage message={poster.error?.message ?? "포스터를 불러오지 못했어요."} />;
  return (
    <PosterResumeGate state={poster.data}>
      <SongAr workId={workId} state={poster.data} refresh={poster.refresh} />
    </PosterResumeGate>
  );
}

type SongMode = "code" | "file" | "none";

function SongAr({ workId, state, refresh }: { workId: string; state: PosterStateResponse; refresh: () => Promise<PosterStateResponse | null> }) {
  const [mode, setMode] = useState<SongMode>(state.music ? (state.music.type === "upload" ? "file" : "code") : "code");
  const [code, setCode] = useState("");
  const codeIme = useImeSafeInput(setCode, (value) => value.replace(/[^A-Za-z2-9]/gu, "").toUpperCase());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mvs, setMvs] = useState<PosterMvCandidate[] | null>(null);
  const [watching, setWatching] = useState<string | null>(null);
  // 「어떻게 움직일까요?」(2026-10-12 용인신촌중 수업) — 비우면 서버의 기본 문장만 쓴다.
  const [motionText, setMotionText] = useState("");
  const fileInput = useRef<HTMLInputElement | null>(null);
  const ar = state.ar ?? { choice: "picture" as const, slot: 1 as PosterSlot };
  const slotWithImage = state.cuts.filter((cut) => cut.image_url);
  const videoCut = ar.choice === "ai_video" ? state.cuts.find((cut) => cut.slot === (ar.slot ?? 1)) : undefined;
  const videoBusy = state.cuts.some((cut) => cut.video_job);

  useEffect(() => {
    void apiRequest<{ items: PosterMvCandidate[] }>(`/api/works/${encodeURIComponent(workId)}/poster/mvs`)
      .then((result) => setMvs(result.items))
      .catch(() => setMvs([]));
  }, [workId]);

  // 🔑 선생님 선물(AI 영상 +2회 등)이 오거나 학생이 확인을 누르면 남은 횟수를 곧바로 다시 읽는다.
  //    종전에는 영상을 만드는 중일 때만 다시 읽어, 새로고침 전까지 「다 썼어요」가 남았다(2026-10-12 용인신촌중).
  //    폴링마다 같은 선물이 다시 발행되므로 «선물 목록이 바뀌었을 때» 만 읽는다.
  //    🚨 다시 읽기에 **성공한 뒤에만** 처리 완료로 적는다 — 한 번 실패하면 다음 폴링 알림에서 다시 시도한다.
  const readGiftRevision = useRef(pendingGiftsRevision());
  const rereading = useRef(false);
  useEffect(() => subscribePendingGifts(() => {
    const revision = pendingGiftsRevision();
    if (revision === readGiftRevision.current || rereading.current) return;
    rereading.current = true;
    void refresh()
      .then((state) => {
        if (state) readGiftRevision.current = revision;
      })
      .finally(() => {
        rereading.current = false;
      });
  }), [refresh]);

  // 영상을 만드는 중이면 5초마다 다시 본다(완성되면 미리보기가 붙는다).
  useEffect(() => {
    if (!videoBusy) return undefined;
    const timer = window.setInterval(() => void refresh(), 5_000);
    return () => window.clearInterval(timer);
  }, [refresh, videoBusy]);

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "요청을 처리하지 못했어요. 다시 눌러 주세요.");
    } finally {
      setBusy(null);
    }
  };

  const importSong = () => run("code", async () => {
    const normalized = code.trim().toUpperCase();
    if (!songCodePattern.test(normalized)) throw new ApiError(422, "INVALID", "코드를 확인해 주세요");
    await apiRequest<{ music_source: MusicSource }>(`/api/works/${encodeURIComponent(workId)}/import-song`, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ code: normalized }),
    });
  });

  const uploadSong = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    void run("file", async () => {
      const form = new FormData();
      form.set("audio", file);
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/upload-song`, { method: "POST", body: form });
    });
  };

  const noSong = () => run("none", async () => {
    setMode("none");
    if (!state.music) return;
    await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
      method: "POST",
      body: JSON.stringify({ step: "pst_song", clear_music: true, gifts_v: 2 }),
    });
  });

  const choose = (choice: "picture" | "ai_video" | "my_mv", extra: { slot?: PosterSlot; mv_work_id?: string } = {}) =>
    run(`ar:${choice}`, async () => {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/poster/ar`, {
        method: "PUT",
        body: JSON.stringify({ choice, ...extra }),
      });
    });

  const makeVideo = (slot: PosterSlot) => run("video", async () => {
    await apiRequest(`/api/works/${encodeURIComponent(workId)}/poster/video`, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify(motionText.trim() ? { slot, prompt: motionText.trim() } : { slot }),
    });
  });

  const overlay = ar.choice === "ai_video" && videoCut?.clip_url
    ? <video src={videoCut.clip_url} muted autoPlay loop playsInline />
    : ar.choice === "my_mv"
      ? <span className="poster-phone-mv">내 뮤직비디오</span>
      : (() => {
        const url = state.cuts.find((cut) => cut.slot === (ar.slot ?? 1))?.image_url;
        return url ? <img src={url} alt="" /> : null;
      })();

  return (
    <StudioShell currentStep={3} wide imageRegenRemaining={state.image_regen_remaining} onStepClick={(step) => void goPosterStep(workId, step)}>
      <div className="poster-songar">
        <div className="poster-songar-main">
          <section className="poster-card">
            <h2><span className="poster-num">1</span>포스터에 노래 붙이기 <span className="poster-tag">선택</span></h2>
            <p className="poster-card-lead">AR 로 포스터를 비추면 이 노래가 같이 나와요.</p>
            <div className="poster-songopts" role="radiogroup" aria-label="노래 붙이는 방법">
              {([
                ["code", "곡 코드로 불러오기", "음악 만들기 수업에서 만든 내 노래"],
                ["file", "음원 파일 올리기", "MP3 · M4A · WAV · 10MB까지"],
                ["none", "노래 없이", "AR 에서 소리가 나지 않아요"],
              ] as const).map(([key, title, body]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={mode === key}
                  className={`poster-songopt${mode === key ? " is-on" : ""}`}
                  // 🔑 「내 노래」 를 가져오는 중엔 방식을 못 바꾼다 — 「노래 없이」 뒤에 늦게 끝난 가져오기가 노래를 다시 붙인다.
                  disabled={busy === "pick"}
                  onClick={() => (key === "none" ? void noSong() : setMode(key))}
                >
                  <b>{title}</b>
                  <small>{body}</small>
                </button>
              ))}
            </div>
            {mode === "code" ? (
              <MySongPicker
                workId={workId}
                selectedTrackId={state.music?.type === "imported" ? state.music.source_track_id : null}
                disabled={busy !== null}
                onBusyChange={(picking) => setBusy(picking ? "pick" : null)}
                onImported={() => refresh()}
              />
            ) : null}
            {/* 🔑 「내 노래」 가 없거나 다른 곡을 쓰려면 코드 입력이 그대로 아래에 있다. */}
            {mode === "code" ? (
              <div className="poster-songcode">
                <input
                  aria-label="곡 코드"
                  className="song-code-input"
                  value={code}
                  maxLength={13}
                  placeholder="ABCD2345EFGH6"
                  autoCapitalize="characters"
                  {...codeIme}
                />
                <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void importSong()}>
                  {busy === "code" ? <InlineSpinner label="불러오는 중..." /> : "불러오기"}
                </button>
              </div>
            ) : null}
            {mode === "file" ? (
              <div className="poster-songfile">
                <input ref={fileInput} className="sr-only" type="file" accept=".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/wav" onChange={uploadSong} />
                <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => fileInput.current?.click()}>
                  {busy === "file" ? <InlineSpinner label="올리는 중..." /> : "음원 파일 고르기"}
                </button>
                <span>내가 만든 노래만 올려요. 다른 사람의 노래는 저작권이 있어요.</span>
              </div>
            ) : null}
            {state.music && mode !== "none" ? (
              <div className="poster-song-chosen" aria-live="polite">
                <span aria-hidden="true">♪</span>
                <div>
                  <b>{state.music.title}</b>
                  <small>{state.music.type === "upload" ? "내 파일" : "곡 코드로 가져옴"} · {duration(state.music.duration_seconds)}</small>
                </div>
              </div>
            ) : null}
          </section>

          <section className="poster-card">
            <h2><span className="poster-num">2</span>AR 로 비추면 무엇이 나올까요?</h2>
            <div className="poster-aropts" role="radiogroup" aria-label="AR 로 나올 것">
              <button type="button" role="radio" aria-checked={ar.choice === "picture"} className={`poster-aropt${ar.choice === "picture" ? " is-on" : ""}`} disabled={busy !== null || slotWithImage.length === 0} onClick={() => void choose("picture", { slot: ar.slot ?? 1 })}>
                {state.cuts[0]?.image_url ? <img src={state.cuts[0].image_url} alt="" /> : <span className="poster-aropt-blank" />}
                <b>그림 + 노래</b>
                <small>포스터 그림이 뜨고 노래가 나와요</small>
                <em className="is-ok">바로 돼요</em>
              </button>
              <button type="button" role="radio" aria-checked={ar.choice === "ai_video"} className={`poster-aropt${ar.choice === "ai_video" ? " is-on" : ""}`} disabled={busy !== null || !state.video_enabled || slotWithImage.length === 0} onClick={() => void choose("ai_video", { slot: ar.slot ?? 1 })}>
                {state.cuts[0]?.image_url ? <img src={state.cuts[0].image_url} alt="" /> : <span className="poster-aropt-blank" />}
                <b>AI 영상 + 노래</b>
                <small>내 그림이 움직이는 영상이 돼요</small>
                <em className="is-info">{state.video_enabled ? "영상 1편 + 다시 1번" : "준비 중"}</em>
              </button>
              <button type="button" role="radio" aria-checked={ar.choice === "my_mv"} className={`poster-aropt${ar.choice === "my_mv" ? " is-on" : ""}`} disabled={busy !== null || !mvs?.length} onClick={() => mvs?.[0] && void choose("my_mv", { mv_work_id: mvs[0].work_id })}>
                {mvs?.[0] ? <video src={mvs[0].url} muted preload="metadata" /> : <span className="poster-aropt-blank" />}
                <b>내 뮤직비디오</b>
                <small>영상 만들기 수업에서 만든 작품</small>
                <em>{mvs?.length ? "고를 수 있어요" : "작품이 있을 때만"}</em>
              </button>
            </div>

            {ar.choice !== "my_mv" ? (
              <div className="poster-ar-slots">
                <p className="poster-field">{ar.choice === "ai_video" ? "어떤 그림을 움직일까요?" : "어떤 그림을 띄울까요?"} <small>(AI 가 그린 그림만 고를 수 있어요)</small></p>
                <div className="poster-ar-slot-grid">
                  {state.cuts.filter((cut) => cut.image_url).map((cut) => (
                    <button
                      key={cut.slot}
                      type="button"
                      className={`poster-ar-slot${(ar.slot ?? 1) === cut.slot ? " is-on" : ""}`}
                      disabled={!cut.image_url || busy !== null}
                      aria-pressed={(ar.slot ?? 1) === cut.slot}
                      onClick={() => void choose(ar.choice, { slot: cut.slot })}
                    >
                      {cut.image_url ? <img src={cut.image_url} alt="" /> : null}
                      <span>{SLOT_TITLE[cut.slot]}</span>
                    </button>
                  ))}
                  {state.photos.length ? <div className="poster-ar-slot is-blocked">올린 사진은 {ar.choice === "ai_video" ? "영상으로 못 만들어요" : "AR 에 못 써요"}</div> : null}
                </div>
                {ar.choice === "ai_video" && videoCut && !videoCut.video_job && state.video_remaining > 0 ? (
                  <div className="poster-motion">
                    <label className="poster-field" htmlFor="poster-motion-input">
                      어떻게 움직일까요? <small>(비워 두면 장면이 자연스럽게 움직여요)</small>
                    </label>
                    <input
                      id="poster-motion-input"
                      value={motionText}
                      maxLength={POSTER_MOTION_MAX_CHARS}
                      placeholder="예: 강아지가 꼬리를 흔들어요"
                      disabled={busy !== null}
                      onChange={(event) => setMotionText(event.target.value)}
                    />
                    <div className="poster-chips">
                      {MOTION_EXAMPLES.map((example) => (
                        <button key={example} type="button" className="poster-chip" disabled={busy !== null} onClick={() => setMotionText(example)}>
                          {example}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {ar.choice === "ai_video" && videoCut ? (
                  <div className="poster-vidstate" aria-live="polite">
                    {videoCut.video_job ? (
                      <>
                        <span className="poster-spin" aria-hidden="true" />
                        <div><b>영상을 만들고 있어요</b><small>보통 1~3분 걸려요. 기다리는 동안 인쇄 확정으로 가도 돼요.</small></div>
                      </>
                    ) : videoCut.clip_url ? (
                      <>
                        <video className="poster-vid-preview" src={videoCut.clip_url} muted autoPlay loop playsInline aria-hidden="true" />
                        <div><b>영상이 완성됐어요</b><small>{state.video_remaining > 0 ? `마음에 안 들면 ${state.video_remaining}번 더 만들 수 있어요.` : "다시 만들기를 다 썼어요."}</small></div>
                        <button className="button-primary" type="button" onClick={() => setWatching(videoCut.clip_url)}>▶ 크게 보기</button>
                        {state.video_remaining > 0 ? (
                          <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void makeVideo(videoCut.slot)}>다시 만들기</button>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div>
                          <b>{SLOT_TITLE[videoCut.slot]} 로 영상을 만들어요</b>
                          <small>{videoCut.video_failed_code ? videoFailureMessage(videoCut.video_failed_code) : `남은 횟수 ${state.video_remaining}번`}</small>
                        </div>
                        <button className="button-primary" type="button" disabled={busy !== null || state.video_remaining <= 0 || !videoCut.image_url} onClick={() => void makeVideo(videoCut.slot)}>
                          {busy === "video" ? <InlineSpinner label="시작하는 중..." /> : "영상 만들기"}
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
                <p className="poster-props-hint">적은 움직임에 더해, 카메라는 늘 천천히 다가가요. 새로 만들 때마다 남은 횟수를 1번 써요.</p>
              </div>
            ) : (
              <div className="poster-ar-slots">
                <p className="poster-field">어떤 뮤직비디오를 띄울까요?</p>
                <div className="poster-ar-slot-grid">
                  {(mvs ?? []).map((item) => (
                    <button key={item.work_id} type="button" className={`poster-ar-slot${ar.mv_work_id === item.work_id ? " is-on" : ""}`} disabled={busy !== null} onClick={() => void choose("my_mv", { mv_work_id: item.work_id })}>
                      <video src={item.url} muted preload="metadata" />
                      <span>{item.session_title}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>
          {error ? <InlineError>{error}</InlineError> : null}
          <footer className="poster-songar-foot">
            <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void goPosterStep(workId, 2)}>← 편집으로</button>
            <button className="button-primary" type="button" disabled={busy !== null} onClick={() => void goPosterStep(workId, 4)}>인쇄 확정하러 가기 →</button>
          </footer>
        </div>
        <aside className="poster-songar-side" aria-label="관람객 폰 미리보기">
          <p className="poster-field">관람객 폰에서는 이렇게 보여요</p>
          <div className="poster-phone">
            <div className="poster-phone-cam">
              <div className="poster-phone-poster"><PosterPreview state={state} /></div>
              <div className="poster-phone-overlay">{overlay}</div>
              {state.music && mode !== "none" ? <div className="poster-phone-song">♪ {state.music.title} · {state.student_name}</div> : null}
            </div>
          </div>
          <p className="poster-props-hint">영상은 포스터 위에 붙지 않고 폰 화면 가운데에 떠요. 키링 AR 과 같은 방식이에요.</p>
        </aside>
      </div>
      {watching ? <PosterVideoDialog clipUrl={watching} musicAssetId={mode !== "none" ? state.music?.asset_id ?? null : null} onClose={() => setWatching(null)} /> : null}
    </StudioShell>
  );
}
